import React, { useRef, useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useMap } from "react-leaflet";
import L from "leaflet";
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import markerShadowPng from "leaflet/dist/images/marker-shadow.png";
import { FaBaby, FaVirus, FaArrowLeft } from "react-icons/fa";

const COLOR_LIGHT = "#dbeafe";
const COLOR_DARK = "#1e3a8a";
const IconRMNCH = ({ className = "w-4 h-4" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none">
        <path d="M20 6l-5 2-5-2-6 2v10l6-2 5 2 5-2 6 2V8L20 6z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconImmun = ({ className = "w-4 h-4" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none">
        <path d="M12 3l7 3v5c0 5-4 9-7 11-3-2-7-6-7-11V6l7-3z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Fix default marker paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIconPng,
    shadowUrl: markerShadowPng,
});
function getCentroid(feature) {
    const layer = L.geoJSON(feature);
    return layer.getBounds().getCenter(); // returns LatLng
}
// Color helpers
function hexToRgb(hex) {
    const clean = hex.replace("#", "");
    const bigint = parseInt(clean, 16);
    return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}
function rgbToHex({ r, g, b }) {
    const toHex = (v) => v.toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
function lerp(a, b, t) {
    return Math.round(a + (b - a) * t);
}
function interpolateHex(a, b, t) {
    const A = hexToRgb(a);
    const B = hexToRgb(b);
    return rgbToHex({ r: lerp(A.r, B.r, t), g: lerp(A.g, B.g, t), b: lerp(A.b, B.b, t) });
}
function getColorForRate(value) {
    const v = Math.max(0, Math.min(100, Number(value || 0)));
    const t = v / 100;
    return interpolateHex(COLOR_LIGHT, COLOR_DARK, t);
}
function getDistrictName(props) {
    return props?.name || props?.DISTRICT || props?.district || "District";
}
// Add dummy rates
function addDummyRatesToDistricts(geojson) {
    if (!geojson || !geojson.features) return geojson;
    const seeded = (s) => {
        let h = 2166136261 >>> 0;
        for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
        return (h >>> 0) / 4294967295;
    };
    return {
        ...geojson,
        features: geojson.features.map((f) => {
            const props = f.properties || {};
            const name = props.name || props.DISTRICT || props.district || "x";
            if (props.anc_rate == null) props.anc_rate = Math.round(15 + seeded(name) * 80);
            if (props.immun_rate == null)
                props.immun_rate = Math.round(15 + ((seeded(name) * 0.73 + 0.27) % 1 * 80));
            f.properties = props;
            return f;
        }),
    };
}
function FlyToState({ districtView, initialView }) {
    const map = useMap();

    useEffect(() => {
        // When districtView becomes null, return to initial state view
        if (!districtView && initialView?.center && initialView?.zoom) {
            map.flyTo(initialView.center, initialView.zoom, {
                animate: true,
                duration: 1.2, // smooth transition
            });

            // Optionally re-apply map bounds and invalidate size after fly
            setTimeout(() => {
                try {
                    map.invalidateSize();
                } catch (e) { }
            }, 800);
        }
    }, [districtView, map, initialView]);

    return null;
}
function FlyToDistrict({ districtView }) {
    const map = useMap();

    useEffect(() => {
        if (districtView) {
            const districtCenter = [20.349221, 74.103980];
            const districtZoom = 9;
            map.flyTo(districtCenter, districtZoom, { animate: true, duration: 1 });
        }
    }, [districtView, map]);

    return null;
}

export default function MaharashtraMap({ onDistrictClick }) {
    const mapRef = useRef();
    const districtLayerRef = useRef();
    const [seed, setSeed] = useState(0);
    const [selectedTab, setSelectedTab] = useState("RMNCH");
    const [hoveredId, setHoveredId] = useState(null);

    const [stateGeoJSON, setStateGeoJSON] = useState(null);
    const [districtsGeoJSON, setDistrictsGeoJSON] = useState(null);

    const [districtView, setDistrictView] = useState(null);
    const [districtGeoJSON, setDistrictGeoJSON] = useState(null);
    const [facilityMarkers, setFacilityMarkers] = useState([]);

    const initialView = useRef({ center: [18.695485, 75.700066], zoom: 6, minZoom: 6, maxZoom: 10 });

    // ---------------- Load GeoJSON ----------------
    useEffect(() => {
        fetch(process.env.PUBLIC_URL + "/geojson/1.geojson") // state polygon
            .then((res) => res.json())
            .then((data) => setStateGeoJSON(data))
            .catch((err) => console.error("State GeoJSON load error:", err));

        fetch(process.env.PUBLIC_URL + "/geojson/1.geojson") // all districts
            .then((res) => res.json())
            .then((data) => setDistrictsGeoJSON(data))
            .catch((err) => console.error("Districts GeoJSON load error:", err));
    }, []);

    // ---------------- District Drilldown ----------------
    useEffect(() => {
        if (!districtView) return;

        fetch(`${process.env.PUBLIC_URL}/geojson/${districtView}.geojson`)
            .then((res) => res.json())
            .then((data) => {
                setDistrictGeoJSON(data);

                // Place 5 facility markers inside district bounds
                const polygon = L.geoJSON(data);
                const bounds = polygon.getBounds();
                const facilities = [
                    { lat: 20.611439, lng: 74.068056, name: "Facility 1" },
                    { lat: 20.403065, lng: 73.790651, name: "Facility 2" },
                    { lat: 20.086106, lng: 74.617372, name: "Facility 3" },
                    { lat: 20.132530, lng: 73.554445, name: "Facility 4" },
                    { lat: 19.670254, lng: 73.645082, name: "Facility 5" },
                ];
                // while (facilities.length < 5) {
                //     const lat = bounds.getSouth() + Math.random() * (bounds.getNorth() - bounds.getSouth());
                //     const lng = bounds.getWest() + Math.random() * (bounds.getEast() - bounds.getWest());
                //     if (polygon.getLayers()[0].getBounds().contains([lat, lng])) {
                //         facilities.push({ lat, lng, name: `Facility ${facilities.length + 1}` });
                //     }
                // }
                setFacilityMarkers(facilities);
            })
            .catch((err) => console.error("District GeoJSON load error:", err));
    }, [districtView]);

    // ---------------- Zoom to district ----------------
    useEffect(() => {
        if (!districtView || !districtGeoJSON || !mapRef.current) return;

        const map = mapRef.current;

        // Remove previous district layer
        if (map._districtLayer) {
            map.removeLayer(map._districtLayer);
        }

        // Add new district layer
        const layer = L.geoJSON(districtGeoJSON, {
            style: { color: "#1e3a8a", weight: 2, fillOpacity: 0.4 },
        }).addTo(map);
        map._districtLayer = layer;

        // ----- Zoom to fixed center and zoom level -----
        const districtCenter = [20.349221, 74.103980]; // fixed lat/lng
        const districtZoom = 9; // adjust as needed
        map.flyTo(districtCenter, districtZoom, {
            animate: true,
            duration: 1.0
        });

        // Place 5 facility markers inside district bounds
        const bounds = layer.getBounds();
        const facilities = [];
        while (facilities.length < 5) {
            const lat = bounds.getSouth() + Math.random() * (bounds.getNorth() - bounds.getSouth());
            const lng = bounds.getWest() + Math.random() * (bounds.getEast() - bounds.getWest());
            if (layer.getLayers()[0].getBounds().contains([lat, lng])) {
                facilities.push({ lat, lng, name: `Facility ${facilities.length + 1}` });
            }
        }
        setFacilityMarkers(facilities);

    }, [districtView, districtGeoJSON]);

    const districtsWithRates = useMemo(() => {
        if (!districtsGeoJSON) return null;
        return addDummyRatesToDistricts(districtsGeoJSON);
    }, [districtsGeoJSON]);

    function computeStyle(feature) {
        const props = feature.properties || {};
        const value = selectedTab === "RMNCH" ? props.anc_rate : props.immun_rate;
        const id = props.name || props.DISTRICT || props.district;
        return {
            color: hoveredId === id ? "#1e40af" : "#ffffff",
            weight: hoveredId === id ? 2 : 1,
            fillColor: getColorForRate(value),
            fillOpacity: hoveredId === id ? 0.95 : 0.9,
        };
    }

    function onEachDistrict(feature, layer) {
        const p = feature.properties || {};
        const id = p.name || p.DISTRICT || p.district;
        const name = p.name || p.DISTRICT || p.district || "District";
        const anc = p.anc_rate;
        const immun = p.immun_rate;

        layer.bindTooltip(`${name}<br/>RMNCH: ${anc}%<br/>NCD: ${immun}%`, { sticky: true });
        layer.on({

            mouseover: () => setHoveredId(id),
            mouseout: () => setHoveredId(null),
            click: () => {
                setDistrictView(name);
                onDistrictClick(name);
                setSeed((s) => s + 1); // <-- bump seed whenever user clicks a district
            },
        });
    }

    const handleBackToState = () => {
        setDistrictView(null);
        setDistrictGeoJSON(null);
        setFacilityMarkers([]);
        setSeed(0);
        if (mapRef.current) {
            const map = mapRef.current;

            // Reset view to initial state
            map.setView(initialView.current.center, initialView.current.zoom);
        }
        if (onDistrictClick) {
            onDistrictClick(null); // null signals "state level" counts
        }
    };

    // ------------------- Legend -------------------
    useEffect(() => {
        if (!mapRef.current) return;

        const map = mapRef.current;

        // Remove previous legend
        if (map._legendControl) map.removeControl(map._legendControl);

        const legend = L.control({ position: "bottomright" });

        legend.onAdd = function () {
            const div = L.DomUtil.create("div", "");
            // Style manually
            div.style.background = "white";
            div.style.padding = "8px";
            div.style.borderRadius = "6px";
            div.style.boxShadow = "0 1px 4px rgba(0,0,0,0.3)";
            div.style.fontSize = "12px";

            // Title
            const title = L.DomUtil.create("div", "", div);
            title.style.fontWeight = "bold";
            title.style.marginBottom = "4px";
            title.innerHTML = `${selectedTab} Rate`;

            // Colors & labels
            const grades = [0, 20, 40, 60, 80, 100];
            grades.forEach((grade, i) => {
                const next = grades[i + 1];
                const color = getColorForRate(grade);
                const item = L.DomUtil.create("div", "", div);
                item.style.display = "flex";
                item.style.alignItems = "center";
                item.style.marginBottom = "4px";
                item.innerHTML = `
                <i style="background:${color}; width:18px; height:18px; display:inline-block; margin-right:5px;"></i>
                ${grade}${next ? `–${next}%` : `+%`}
            `;
            });

            return div;
        };

        legend.addTo(map);
        map._legendControl = legend;

    }, [selectedTab, mapRef.current]); // ensure map exists

    return (
        <div className="relative">
            <div className="bg-white rounded-lg shadow-sm p-0 mt-0">

                {/* <div className="flex items-center gap-3 mb-2">
                <button
                    onClick={() => setSelectedTab("RMNCH")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition ${selectedTab === "RMNCH"
                            ? "bg-indigo-600 text-white shadow"
                            : "bg-white text-slate-700 hover:bg-slate-100"
                        }`}
                >
                    <IconRMNCH className={selectedTab === "RMNCH" ? "w-5 h-5 text-white" : "w-5 h-5 text-indigo-600"} />
                    <span>RMNCH</span>
                </button>

                <button
                    onClick={() => setSelectedTab("NCD")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition ${selectedTab === "NCD"
                            ? "bg-indigo-600 text-white shadow"
                            : "bg-white text-slate-700 hover:bg-slate-100"
                        }`}
                >
                    <IconImmun className={selectedTab === "NCD" ? "w-5 h-5 text-white" : "w-5 h-5 text-indigo-600"} />
                    <span>NCD</span>
                </button>
                {districtView && (
                    <button
                        onClick={handleBackToState}
                        className="ml-auto px-4 py-1 bg-gray-200 text-slate-800 rounded-md text-sm font-medium"
                    >
                        Back to State
                    </button>
                )}
            </div> */}

                <div className="w-full rounded-lg overflow-hidden border relative classMapContainer" style={{ height: 390, zIndex: 1 }}>
                    {/* {districtView && (
                        <button
                            onClick={handleBackToState}
                            className="ml-2 px-4 py-2 bg-gray-200 text-slate-800 rounded-md text-sm font-medium flex items-center gap-1"
                        >
                            <FaArrowLeft /> Back to State
                        </button>
                    )} */}
                    <MapContainer
                        whenCreated={(mapInstance) => {
                            mapRef.current = mapInstance;

                            // Set max zoom & restrict bounds
                            const bounds = stateGeoJSON ? L.geoJSON(stateGeoJSON).getBounds() : L.latLngBounds([19, 75], [20, 76]);
                            mapInstance.setMaxBounds(bounds); // cannot pan outside
                            mapInstance.setMinZoom(6);        // minimum zoom level
                            mapInstance.setMaxZoom(10);       // maximum zoom level                        
                            mapInstance.on("drag", () => {
                                if (!bounds.contains(mapInstance.getCenter())) {
                                    mapInstance.panInsideBounds(bounds, { animate: false });
                                }
                            });
                        }}
                        center={initialView.current.center}
                        zoom={initialView.current.zoom}
                        minZoom={6}
                        maxZoom={9}
                        style={{ height: "100%", width: "100%" }}
                    >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />

                        {!districtView && stateGeoJSON && (
                            <GeoJSON data={stateGeoJSON} style={{ color: "#ffffff", weight: 1, fillOpacity: 0.02 }} />
                        )}

                        {!districtView && districtsWithRates && (
                            <GeoJSON data={districtsWithRates} style={computeStyle} onEachFeature={onEachDistrict} />
                        )}

                        {districtView && districtGeoJSON && (
                            <GeoJSON
                                data={districtGeoJSON}
                                style={{ color: "#1e3a8a", weight: 2, fillOpacity: 0.4 }}
                                ref={districtLayerRef}
                            />
                        )}

                        {districtView &&
                            facilityMarkers.map((f, i) => (
                                <Marker key={i} position={[f.lat, f.lng]}>
                                    <Popup>{f.name}</Popup>
                                </Marker>
                            ))}
                        {!districtView && districtsWithRates &&
                            districtsWithRates.features.map((feature, idx) => {
                                const centroid = getCentroid(feature);
                                const name = getDistrictName(feature.properties);
                                return (
                                    <Marker
                                        key={idx}
                                        position={centroid}
                                        interactive={false} // disables mouse interaction
                                        icon={L.divIcon({
                                            className: "district-label-small",
                                            html: `<div style="font-size:10px;; color:black; text-align:center;">${name}</div>`,
                                        })}
                                    />
                                );
                            })
                        }
                        {districtView && (
                            <button
                                onClick={handleBackToState}
                                style={{
                                    position: "absolute",
                                    top: 12,
                                    right: 12,
                                    zIndex: 1000,
                                    padding: "6px 12px",
                                    background: "white",
                                    border: "1px solid #ccc",
                                    borderRadius: 6,
                                    fontSize: 12,
                                    fontWeight: 500,
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                    cursor: "pointer",
                                }}
                            >
                                Back to State
                            </button>
                        )}
                        <div
                            style={{
                                position: "absolute",
                                right: 12,
                                bottom: 12,
                                zIndex: 1000,
                                background: "rgba(255,255,255,0.96)",
                                padding: "8px 10px",
                                borderRadius: 8,
                                boxShadow: "0 6px 18px rgba(15,23,42,0.12)",
                                fontSize: 12,
                                minWidth: 100,
                                display: "flex",
                                flexDirection: "column",
                                gap: 4,
                            }}
                        >
                            <div style={{ fontWeight: 700, marginBottom: 6 }}>
                                {selectedTab === "RMNCH" ? "RMNCH (%)" : "NCD (%)"}
                            </div>
                            {[0, 20, 40, 60, 80, 100].map((s) => (
                                <div key={s} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <div
                                        style={{
                                            width: 24,
                                            height: 12,
                                            borderRadius: 3,
                                            background: getColorForRate(s),
                                            border: "1px solid rgba(0,0,0,0.06)",
                                        }}
                                    />
                                    <div style={{ minWidth: 20 }}>{s}</div>
                                </div>
                            ))}
                        </div>
                        <FlyToDistrict districtView={districtView} />
                        <FlyToState districtView={districtView} initialView={initialView.current} />
                    </MapContainer>

                </div>
                <div className="flex justify-content-end items-center gap-3 my-1 pb-1 px-2 ">
                    <button
                        onClick={() => setSelectedTab("RMNCH")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-2 text-sm font-medium transition ${selectedTab === "RMNCH"
                            ? "bg-indigo-600 text-white  border border-slate-100 shadow"
                            : "bg-white  border border-slate-100 text-slate-700 hover:bg-slate-100"
                            }`}
                    >

                        <FaBaby className={selectedTab === "RMNCH" ? "w-5 h-5 text-white" : "w-5 h-5 text-indigo-600"} />
                        <span>RMNCH</span>
                    </button>

                    <button
                        onClick={() => setSelectedTab("NCD")}
                        className={`flex items-center gap-2 px-4 py-2  rounded-2 text-sm font-medium transition ${selectedTab === "NCD"
                            ? "bg-indigo-600 text-white  border border-slate-100 shadow"
                            : "bg-white  border border-slate-100 text-slate-700 hover:bg-slate-100"
                            }`}
                    >

                        <FaVirus className={selectedTab === "NCD" ? "w-5 h-5 text-white" : "w-5 h-5 text-indigo-600"} />
                        <span>NCD</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
